import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/db78il3askivmpnrchl0";

type Status = "idle" | "submitting" | "success" | "error";

const inputClass =
  "w-full h-11 px-4 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [messageLen, setMessageLen] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      formEl.reset();
      setMessageLen(0);
      return;
    }

    formData.delete("website_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string") payload.append(key, value);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        formEl.reset();
        setMessageLen(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center justify-center text-center py-14 px-4 bg-background-50 border border-background-200 rounded-lg">
        <span className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center text-primary-600 mb-5">
          <i className="ri-checkbox-circle-line text-3xl"></i>
        </span>
        <h3 className="font-heading font-bold text-lg text-foreground-900">反馈已提交</h3>
        <p className="mt-2 text-sm text-foreground-500 max-w-md leading-relaxed">
          感谢你对《年轻的母亲3》资料站的支持，我们会认真阅读每一条建议并持续完善内容。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 px-5 py-2.5 rounded-md bg-background-100 text-foreground-800 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-8"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-2">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的昵称"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-2">
            邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复，例如 name@example.com"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="fb-subject" className="block text-sm font-medium text-foreground-800 mb-2">
          反馈类型
        </label>
        <select id="fb-subject" name="subject" defaultValue="内容建议" className={`${inputClass} cursor-pointer`}>
          <option value="内容建议">内容建议</option>
          <option value="剧情纠错">剧情纠错</option>
          <option value="角色讨论">角色讨论</option>
          <option value="合作与投稿">合作与投稿</option>
          <option value="其他">其他</option>
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-2">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          onChange={(e) => setMessageLen(e.target.value.length)}
          placeholder="请描述你的建议或想法，最多 500 字…"
          className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
        ></textarea>
        <p className="mt-1.5 text-xs text-foreground-400 text-right">{messageLen} / 500</p>
      </div>

      <div className="form-honeypot-trap" aria-hidden="true">
        <label htmlFor="fb-website-alt">请勿填写此项</label>
        <input
          id="fb-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && formError && (
        <div className="mt-5 px-4 py-3 rounded-md bg-accent-50 border border-accent-200 text-sm text-accent-800 flex items-start gap-2">
          <span className="w-4 h-4 flex items-center justify-center mt-0.5"><i className="ri-error-warning-line"></i></span>
          <span>{formError}</span>
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
        <p className="text-xs text-foreground-400 leading-relaxed">
          我们仅将你填写的信息用于回复反馈，不会用于其他用途。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="w-full sm:w-auto shrink-0 inline-flex items-center justify-center gap-2 px-7 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? (
            <>
              <span className="w-4 h-4 flex items-center justify-center animate-spin"><i className="ri-loader-4-line"></i></span>
              提交中…
            </>
          ) : (
            <>
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line"></i></span>
              提交反馈
            </>
          )}
        </button>
      </div>
    </form>
  );
}